import { observable, action, computed } from 'mobx';
import objectPath from 'object-path';
import Alert from 'react-s-alert';
import { throttle } from 'throttle-debounce';

import { ArrayApp } from 'core';
import { analytics } from 'core/analytics';
import { FieldStoreRegistry } from 'core/registry';
import { Validators } from 'libs/forms/validators';
import { EditorStore } from 'libs/editor/store';
import { html } from 'libs/editor/serializers/html';
import { markdown } from 'libs/editor/serializers/markdown';

import { clientStore } from 'apps/client/app';
import FileAppManager from 'apps/file/appManager';

import i18next from 'i18next';

export class FieldStore {
  disposers = [];

  @observable formStore;
  @observable fieldName;
  @observable isFocussed = false;
  @observable defaultValue;
  @observable serverValidation = null;
  @observable finished = false;
  @observable optionsManager = null;
  @observable ref = null;
  @observable draftEnabled = true;
  @observable hide = false;

  // Use dependsOn to indicate that there is another field
  // in the formStore that is required for showing it
  @observable dependsOn = null;

  isDraftModified = false;
  initialized = false;

  constructor(formStore, fieldConf, nextFieldName) {
    this.formStore = formStore;
    this.fieldName = fieldConf.fieldName;
    this.nextFieldName = nextFieldName;
    this.conf = fieldConf;
    this.draftThrottle = this.conf.draftThrottle || 5000;
    this.draftEnabled = !this.conf.draftDisabled;
    this.dependsOn = fieldConf.dependsOn || null;
    this.ref = null;
    if (this.draftEnabled) {
      const draft = this.getDraft();
      if (draft) this.restoreDraft();
      this.throttledSaveDraft = throttle(this.draftThrottle, this.saveDraft);
    }
  }

  @computed get target() {
    if (this.conf.targetFieldName) {
      return objectPath.get(this.formStore.target, this.conf.targetFieldName);
    }
    return this.formStore.target;
  }

  @computed get draftKey() {
    const draftKeyComponents = [this.formStore.formName, this.fieldName];
    if (this.target && this.target.inSpace) {
      draftKeyComponents.unshift(this.target.inSpace.id);
    }
    if (clientStore.signedInProfile && clientStore.signedInProfile.username) {
      draftKeyComponents.unshift(clientStore.signedInProfile.username);
    }
    return draftKeyComponents.filter(e => !!e).join('-');
  }

  @action getDraft = () => {
    try {
      const serializedDraft = sessionStorage.getItem(this.draftKey);
      if (!serializedDraft) return null;
      const draft = serializedDraft;
      if (!draft) return null;
      return draft;
    } catch (error) {
      analytics.logError(error);
    }
    return null;
  };

  @action restoreDraft = () => {
    if (!(typeof this.value === 'string')) return;
    const draft = this.getDraft();
    if (!(draft && typeof draft === 'string')) return;
    this.target[this.fieldName] = draft;
    if (this.editor) {
      this.editor.value = this.deserialize(draft);
    }
    this.isDraftModified = false;
  };

  @action saveDraft = () => {
    if (!(typeof this.value === 'string')) return;
    try {
      sessionStorage.setItem(this.draftKey, this.value);
    } catch (error) {
      analytics.logError(error);
    }
  };

  @action clearDraft = () => {
    if (!this.draftEnabled) return;
    try {
      this.throttledSaveDraft.cancel();
      sessionStorage.removeItem(this.draftKey);
    } catch (error) {
      analytics.logError(error);
    }
  };

  @action handleBlur = () => {
    if (!this.draftEnabled) return;
    this.throttledSaveDraft.cancel();
    if (this.isDraftModified) {
      this.saveDraft();
    }
  };

  @computed get value() {
    if (!(this.fieldName in this.target)) {
      const { fieldName } = this;
      const { name } = this.target.constructor;
      console.warn(
        i18next.t(
          "Trying to access '{{fieldName}}' which does not exist on {{name}}. Check your form config.",
          { fieldName, name },
        ),
      );
    }
    return this.target[this.fieldName];
  }

  @computed get validity() {
    return this.validate(this);
  }

  prepare() {
    // no-op
  }

  validate() {
    if (this.conf.optional && this.isEmpty) {
      return [true, ''];
    }
    if (this.stillRequired) {
      return [
        false,
        i18next.t('{{label}} is required', { label: this.conf.label }),
      ];
    }
    if (this.serverValidation) {
      return [false, `${this.conf.label} ${this.serverValidation.message}`];
    }
    return [null, ''];
  }

  @computed get stillRequired() {
    return !this.conf.optional && this.isEmpty;
  }

  @computed get showValidation() {
    if (this.isEmpty) {
      if (this.formStore.isFocussed) {
        return false;
      }
      if (this.conf.optional) {
        return false;
      }
    }
    if (this.isFocussed && !this.serverValidation) {
      return false;
    }
    return true;
  }

  @action SetServerValidation = err => {
    this.serverValidation = err;
  };

  @action SetFocus = bool => {
    this.isFocussed = bool;
    if (this.isFocussed) {
      this.ref?.focus();
    }
  };

  @action SetRef = ref => {
    this.ref = ref;
  };

  @action UpdateVal = val => {
    if (this.serverValidation && this.target[this.fieldName] !== val) {
      this.serverValidation = null;
    }
    this.target[this.fieldName] = val;
    if (this.draftEnabled) {
      if (this.initialized) {
        this.isDraftModified = true;
        this.throttledSaveDraft();
      } else {
        this.initialized = true;
      }
    }
  };

  alert = () => {
    if (this.validity[0] === false && !this.stillRequired) {
      Alert.error(this.validity[1]);
    }
  };

  @action Select = selected => {
    this.UpdateValue(selected[this.conf.appKey]);
  };

  @computed get getSelectedOption() {
    // When form is initialised this.optionsManager may be null;
    if (!this.optionsManager) {
      return null;
    }
    // e.g. for StringFieldStore or BoolFieldStore
    if (this.optionsManager.optionExists(this.conf.appKey, this.value)) {
      return this.optionsManager.selectables.find(
        opt => opt[this.conf.appKey] === this.value,
      );
    }
    return null;
  }
}

export class BoolFieldStore extends FieldStore {
  @computed get isEmpty() {
    return this.value !== true;
  }

  // FIXME - Does a boolean field need to be validated?
  validate() {
    const fieldConf = this.conf;
    const super_valid = super.validate();
    if (super_valid[0] !== null) {
      return super_valid;
    }
    if (fieldConf.validator) {
      return Validators[fieldConf.validator](this);
    }
    return [true, ''];
  }

  @action UpdateValue = val => {
    this.UpdateVal(!!val);
  };
}

export class StringFieldStore extends FieldStore {
  /* can't update observables on construction of an app (which happens in a render method)
  init() {
    if (this.conf.fixedValue && (this.value === undefined || this.value.length <= this.conf.fixedValue.length)) {
      this.target[this.fieldName] = this.conf.fixedValue;
    }
  } */
  @computed get isEmpty() {
    if (!this.value) {
      return true;
    }
    let valLen = this.value.length;
    if (this.conf.fixedValue) {
      valLen -= this.conf.fixedValue.length;
    }
    if (valLen === 0) {
      return true;
    }
    return false;
  }

  validate() {
    const fieldConf = this.conf;
    const { value } = this;
    const super_valid = super.validate();
    const { label, minLen, maxLen } = fieldConf;
    if (super_valid[0] !== null) {
      return super_valid;
    }
    if (fieldConf.minLen && value.length < fieldConf.minLen) {
      return [
        false,
        i18next.t('{{label}} needs to be at least {{minLen}} characters long', {
          label,
          minLen,
        }),
      ];
    }
    if (fieldConf.maxLen && value.length > fieldConf.maxLen) {
      return [
        false,
        i18next.t("{{label}} can't be more than {{maxLen}} characters long", {
          label,
          maxLen,
        }),
      ];
    }
    if (fieldConf.validator) {
      return Validators[fieldConf.validator](this);
    }
    return [true, ''];
  }

  @action ResetValue = () => {
    // OR this.target.locals.delete(this.fieldName) ?
    if (this.conf.fixedValue) {
      this.target[this.fieldName] = this.conf.fixedValue;
    } else {
      this.target[this.fieldName] = '';
    }
  };

  @action UpdateValue = val => {
    if (this.conf.fixedValue) {
      if (val.length <= this.conf.fixedValue.length) {
        // eslint-disable-next-line no-param-reassign
        val = this.conf.fixedValue;
      }
    } else if (this.conf.prefixValue) {
      // eslint-disable-next-line no-param-reassign
      val = val.slice(this.conf.prefixValue.length);
    }
    this.UpdateVal(val);
  };
}

export class PhoneFieldStore extends FieldStore {
  @observable libPhone = null;

  constructor(formStore, fieldConf) {
    super(formStore, fieldConf);
    this.importLibPhoneNumber();
  }

  async importLibPhoneNumber() {
    this.libPhone = await import('libphonenumber-js');
  }

  @action ResetValue = () => {
    this.UpdateVal('+');
  };

  @action UpdateValue = val => {
    if (!this.libPhone) {
      this.UpdateVal('');
      return;
    }
    const { validatePhoneNumberLength, AsYouType } = this.libPhone;
    let num = val;
    let asYouType = new AsYouType();
    num = asYouType.input(num);
    let phoneNumber = asYouType.getNumber();
    if (phoneNumber) {
      // if its too long, don't add more numbers
      if (
        validatePhoneNumberLength(num) === 'TOO_LONG' ||
        validatePhoneNumberLength(num, phoneNumber.country) === 'TOO_LONG'
      ) {
        asYouType = new AsYouType();
        num = asYouType.input(num.slice(0, -1));
        phoneNumber = asYouType.getNumber();
      }
      this.UpdateVal(num);
    } else if (num.length <= 5) {
      if (num[0] === '+') {
        num = num.slice(1);
      }
      if (num.length > 0) {
        num = parseInt(num.slice(0, 4), 10);
        if (Number.isNaN(num)) {
          num = '';
        }
      }
      this.UpdateVal(`+${num}`);
    } else {
      this.UpdateVal('+');
    }
  };

  @computed get isEmpty() {
    if (this.value === '+') {
      return true;
    }
    return !this.value;
  }

  validate() {
    if (!this.libPhone) {
      return [false, 'Loading data'];
    }
    const { AsYouType } = this.libPhone;
    const super_valid = super.validate();
    if (super_valid[0] !== null) {
      return super_valid;
    }

    const asYouType = new AsYouType();
    asYouType.input(this.value);
    const isValid = asYouType.getNumber()?.isValid();
    if (!isValid) {
      return [false, 'Invalid Phone Number.'];
    }
    return [true, ''];
  }
}

// const Validation = withConditional(ValidationComponent);
function pad(num, size) {
  const s = `0000${num}`;
  return s.slice(s.length - size);
}

export class IntegerFieldStore extends StringFieldStore {
  @action UpdateValue = val => {
    /* Duplication of the above due to lack of iterative .super() in Babel */
    if (this.conf.fixedValue) {
      if (val.length <= this.conf.fixedValue.length) {
        // eslint-disable-next-line no-param-reassign
        val = this.conf.fixedValue;
      }
    } else if (this.conf.prefixValue) {
      // eslint-disable-next-line no-param-reassign
      val = val.slice(this.conf.prefixValue.length);
    }
    // TODO: None-working - requires refactoring
    // if (this.conf.suffixValue) {
    //   const end = val.length - this.conf.suffixValue.length;
    //
    //   e.target.setSelectionRange(end, end, 'forward');
    //   // eslint-disable-next-line no-param-reassign
    //   val = val.slice(0, val.indexOf(this.conf.suffixValue));
    // }
    if (!val) {
      this.UpdateVal('');
    }
    let parsed = parseInt(val, 10);
    if (!Number.isNaN(parsed)) {
      if (this.conf.minVal && parsed < this.conf.minVal) {
        parsed = this.conf.minVal;
      }
      if (this.conf.maxVal && parsed > this.conf.maxVal) {
        parsed = this.conf.maxVal;
      }
      this.UpdateVal(parsed);
    }
  };

  validate() {
    return super.validate();
  }
}
export class DecimalFieldStore extends IntegerFieldStore {
  @observable inputVal = ''; // temporary state for decimal field.

  updateValue = val => {
    /* We store integers in the fieldStore, unless it's empty, then store an empty string */
    const [pounds, pence] = val.split('.');
    if (!pounds && !pence) {
      this.UpdateValue('');
    } else if (!pence) {
      this.UpdateValue(Math.imul(parseInt(pounds, 10), 100));
    } else {
      // pounds and pence
      let penceInt;
      if (pence.length >= 2) {
        penceInt = parseInt(pence.slice(0, 2), 10);
      } else if (pence.length === 1) {
        penceInt = Math.imul(parseInt(pence, 10), 10);
      }
      const total = Math.imul(parseInt(pounds, 10), 100) + penceInt;
      this.UpdateValue(total);
    }
  };

  updateInputVal = (val, numVal) => {
    // remove all non numeric or '.' values
    // max 2 decimal places
    if (val === undefined) {
      return;
    }

    // bad fix to work around chakra ui issue that injects massive numbers on some
    // keystrokes like "End" instead of moving the cursor
    if (Math.abs(numVal) > 1e12) return;
    if (typeof val !== 'string') val += '';

    val = val.replace('-', '');
    val = val.replace('e', '');
    const currency = val.split('.');
    let [, pence] = currency;
    if (pence) {
      pence = pence.slice(0, 2);
      this.inputVal = [currency[0], pence].join('.');
    } else {
      this.inputVal = val;
    }
  };

  valFromStore = () => {
    let { value } = this;
    const parsed = parseInt(value, 10);
    if (parsed) {
      const pounds = Math.floor(parsed / 100).toString();
      const pence = pad(parsed % 100, 2);
      value = `${pounds}.${pence}`;
    }
    this.updateInputVal(value);
  };

  prepare() {
    // Update from inputValue on form submit.
    this.updateValue(this.inputVal);
  }
}

export class ReferenceFieldStore extends FieldStore {
  @observable inputValue = '';

  constructor(formStore, fieldConf) {
    super(formStore, fieldConf);
    this.prefix = this.conf.prefixValue || '';
  }

  @computed get isEmpty() {
    if (this.nullOption === undefined) {
      return !this.value;
    }
    return this.value !== this.nullOption;
  }

  @action Select = selected => {
    this.UpdateValue(selected);
  };

  @action UpdateValue = val => {
    this.UpdateVal(val);
    this.ClearInput(); // strangely this causes the selected value to reset!
  };

  @action ResetValue = () => {
    if (this.nullOption) {
      this.target[this.fieldName] = this.nullOption;
    } else {
      this.target[this.fieldName] = null;
    }
  };

  @computed get nullOption() {
    return this.optionsManager?.options?.find(opt => opt.isNullOption);
  }

  @computed get getSelectedOption() {
    // 'this.optionsManager' can be null because this gets called
    // whilst the optionsManager is being constructed
    if (!this.value || !this.optionsManager) {
      return null;
    }
    if (
      this.conf.autocomplete ||
      this.optionsManager.optionExists(this.conf.appKey, this.value)
    ) {
      // The selectedOption is the appKey attribute of the Selected Object
      return this.value;
    }
    return null;
  }

  /* For autocomplete */
  @action UpdateInputValue(newValue = '') {
    // Can't call super on or in an arrow function (in babel) :-(
    if (newValue.length <= this.prefix.length) {
      this.inputValue = this.prefix;
    }
    this.inputValue = newValue.slice(this.prefix.length);
  }

  @action ClearInput = () => {
    this.UpdateInputValue();
  };
}

export class ArrayFieldStore extends FieldStore {
  @observable inputValue;

  constructor(formStore, fieldConf) {
    super(formStore, fieldConf);
    this.prefix = this.conf.prefixValue || '';
    this.inputValue = '';
    this.newValueValidator = fieldConf.newValueValidator;
  }

  @computed get isEmpty() {
    return this.getSelectedOption.length === 0;
  }

  prepare() {
    // Add inputValue on form submit if it exists.
    if (this.inputValue.length !== 0) {
      this.Add();
    }
  }

  @computed get isInputValid() {
    if (!this.newValueValidator) {
      return true;
    }
    return Validators[this.newValueValidator]({ value: this.inputValue })[0];
  }

  validate() {
    const fieldConf = this.conf;
    const super_valid = super.validate();
    if (super_valid[0] !== null) {
      return super_valid;
    }
    const { maxLen, label } = fieldConf;
    if (fieldConf.maxLen && this.getSelectedOption.length > fieldConf.maxLen) {
      return [
        false,
        i18next.t("You can't select more than {{maxLen}} {{label}}", {
          maxLen,
          label,
        }),
      ];
    }
    return [true, ''];
  }

  @action UpdateInputValue(newValue = '') {
    // Can't call super on or in an arrow function (in babel) :-(
    if (newValue.length <= this.prefix.length) {
      this.inputValue = this.prefix;
    }
    this.inputValue = newValue.slice(this.prefix.length);
  }

  @action ClearInput = () => {
    this.UpdateInputValue();
  };

  @computed get getSelectedOption() {
    // Currently only works where array elements are Javascript Objects.
    // Not e.g. an array of strings/bools.
    // => Should call optionExists on each value?
    return this.value ? this.value.filter(item => !item.deleted) : [];
  }

  isSelected(opt) {
    const { appKey } = this.conf;
    return this.getSelectedOption.some(v => opt[appKey] === v[appKey]);
  }

  @computed get deletedItemCount() {
    return this.value
      ? this.value.filter(
          item => objectPath.get(item, 'deleted', false) === true,
        ).length
      : 0;
  }

  @action Select = selected => {
    if (this.value.length - this.deletedItemCount === this.conf.maxLen) {
      const { maxLen, itemName } = this.conf;
      Alert.error(
        i18next.t("You can't add more than {{maxLen}} {{itemName}}s", {
          maxLen,
          itemName,
        }),
      );
    } else {
      this.Add(selected);
    }
    if (this.getSelectedOption.length === this.conf.maxLen) {
      this.optionsManager.ToggleShow(false);
    }
  };

  @action Add = (selected = null) => {
    const App = this.value.constructor.ElementApp;
    if (!selected) {
      if (this.inputValue === '' || !this.isInputValid) {
        return;
      }
      if (this.value instanceof ArrayApp) {
        this.value.Add(
          new App(this.value, { [this.conf.appKey]: this.inputValue }),
        );
      } else {
        this.value.push(this.inputValue);
      }
    } else if (this.value instanceof ArrayApp) {
      // FIXME - ArrayApp doesn't implement "Add" by default
      if (selected.constructor !== App) {
        // e.g. Where the selected item is a channel but `You can't add more than {maxLen} {item}s`we are adding Tags
        this.value.Add(
          new App(this.value, {
            [this.conf.appKey]: selected[this.conf.appKey],
          }),
        );
      } else {
        this.value.Add(selected);
      }
    } else {
      this.value.push(selected);
    }
    this.ClearInput();
    if (this.serverValidation) {
      this.serverValidation = null;
    }
  };

  @action Delete = element => {
    if (this.value instanceof ArrayApp) {
      // FIXME - ArrayApp doesn't implement "Delete" by default
      this.value.Delete(element);
      if (this.serverValidation) {
        this.serverValidation = null;
      }
    } else {
      const index = this.value.indexOf(element);
      if (index !== -1) this.value.splice(index, 1);
    }
  };
}

export class PasswordFieldStore extends StringFieldStore {
  @observable show = false;

  @action ToggleShow = () => {
    this.show = !this.show;
  };
}

export class FileFieldStore extends FieldStore {
  @observable prompt = '';
  @observable draggingFile = false;
  @observable fileModified = false;

  constructor(formStore, fieldConf) {
    super(formStore, fieldConf);
    this.promptStr = '';
    // these conf variables should be translated where (and if) they are defined
    // here we only dynamically which strings might exist (through usage) on staging sending strings to locize.
    // Requires yaml config to be migrated to js config
    if (this.conf.prompt) {
      this.promptStr = i18next.t(this.conf.prompt);
    }
    this.draggingPromptStr = '';
    if (this.conf.draggingPrompt) {
      this.draggingPromptStr = i18next.t(this.conf.draggingPrompt);
    }
    const t = formStore.target;
    if (!t.inSpace) {
      const { name } = this.constructor;
      const targetName = t.constructor.registryName || t.constructor.name;
      // what about adding an image to global space target e.g. Post (space=null)?
      throw i18next.t(
        "As the target of {{name}}, {{targetName}} must have a 'inSpace' reference",
        { name, targetName },
      );
    }
    // what if t.inSpace changes? Better to pass the target?
    this.fileAppManager = new FileAppManager(null, { formTarget: t });
    if (this.value) {
      this.fileAppManager.file = this.value;
    }
  }

  prepare() {
    this.fileAppManager.SetActive(false);
  }

  @computed get isEmpty() {
    if (!this.conf.requireFileModel) {
      return !this.value || !this.value.filename;
    }
    return !this.value?.id;
  }

  validate() {
    const super_valid = super.validate();
    if (super_valid[0] !== null) {
      return super_valid;
    }
    if (
      this.fileAppManager.localFile &&
      !this.fileAppManager.fileModelCreated &&
      this.requireFileModel
    ) {
      return [false, i18next.t('Upload not yet initiated, please try again')];
    }
    return [true, ''];
  }

  // Should a lot of the below be on FileApp or FileAppManager?
  @action MarkFileModified = () => {
    this.fileModified = true;
  };

  @action Dragging = () => {
    this.draggingFile = true;
    this.prompt = this.draggingPromptStr;
  };

  @action StopDragging = () => {
    this.draggingFile = false;
    this.prompt = this.promptStr;
  };

  @action Drop = (accepted, rejected, upload = true, callback) => {
    if (accepted.length !== 0) {
      this.fileModified = true;
      if (upload) {
        clientStore.uploader.QueueFile(
          this.fileAppManager,
          accepted[0],
          callback,
        );
      } else {
        this.fileAppManager.SetLocalFile(accepted[0].fileObject);
      }
      this.UpdateVal(this.fileAppManager.file);
    } else if (rejected.length !== 0) {
      Alert.error(
        i18next.t("Sorry, this file format isn't supported for now."),
      );
    }
    this.draggingFile = false;
    this.prompt = this.promptStr;
  };

  @action RemoveFile = () => {
    if (this.fileAppManager.localFile) {
      this.fileAppManager.Revoke(this.fileAppManager.localFile.filename);
    }
    this.fileModified = false;
    this.UpdateVal(null);
  };
}

// FIXME - Is this used?
export class OptionFieldStore extends FieldStore {
  @computed get value() {
    return '';
  }

  @computed get isEmpty() {
    return false;
  }

  validate() {
    return [true, ''];
  }
}

export class EditorFieldStore extends StringFieldStore {
  @observable editor;

  constructor(formStore, fieldConf) {
    super(formStore, fieldConf);
    this.serialize = html.serialize;
    this.deserialize = html.deserialize;
    if (this.conf.serialiser === 'markdown') {
      this.serialize = markdown.serialize.bind(markdown);
      this.deserialize = markdown.deserialize.bind(markdown);
    }
    this.editor = new EditorStore({
      initialValue: this.value && this.deserialize(this.value),
      target: this.formStore.target,
      formatting: this.conf.formatting,
      showMediaMenu: this.conf.showMediaMenu,
      embedDisabled: this.conf.embedDisabled,
      onChange: this.UpdateValue,
      readOnly: this.conf.readOnly === true,
      placeholder: this.conf.placeholder && i18next.t(this.conf.placeholder),
    });
  }

  @computed get isEmpty() {
    const { value } = this.editor;
    let { length } = value.document.text;
    if (this.conf.fixedValue) {
      length -= this.conf.fixedValue.length;
    }
    return length === 0;
  }

  @action SetFocus = bool => {
    const focusChange = this.editor.reference;
    if (!focusChange) {
      this.isFocussed = bool;
      return;
    }
    if (this.isFocussed && bool) {
      focusChange.moveFocusToEndOfDocument();
    }
    if (bool) {
      focusChange.focus();
    } else {
      focusChange.blur();
    }
    this.isFocussed = bool;
    this.editor.onChange(focusChange);
  };

  validate() {
    const fieldConf = this.conf;
    // If there is an image or a video in the field, it's a valid comment.
    // Even if a text-based min/maxLen was set...?
    const value = this.value.replace(/<\/?[^>]+(>|$)/g, '');
    const { label, minLen, maxLen } = fieldConf;
    if (fieldConf.minLen && value.length < fieldConf.minLen) {
      return [
        false,
        i18next.t('{{label}} needs to be at least {{minLen}} characters long', {
          label,
          minLen,
        }),
      ];
    }
    if (fieldConf.maxLen && value.length > fieldConf.maxLen) {
      return [
        false,
        i18next.t("{{label}} can't be more than {{maxLen}} characters long", {
          label,
          maxLen,
        }),
      ];
    }
    if (fieldConf.validator) {
      return Validators[fieldConf.validator](this);
    }
    return [true, ''];
  }

  @action UpdateValue = value => {
    this.UpdateVal(this.serialize(value));
  };

  @action ResetValue = () => {
    this.editor.ResetValue();
  };
}

FieldStoreRegistry.add('forms.StringFieldStore', StringFieldStore);
FieldStoreRegistry.add('forms.IntegerFieldStore', IntegerFieldStore);
FieldStoreRegistry.add('forms.DecimalFieldStore', DecimalFieldStore);
FieldStoreRegistry.add('forms.PhoneFieldStore', PhoneFieldStore);
FieldStoreRegistry.add('forms.ReferenceFieldStore', ReferenceFieldStore);
FieldStoreRegistry.add('forms.BoolFieldStore', BoolFieldStore);
FieldStoreRegistry.add('forms.FileFieldStore', FileFieldStore);
FieldStoreRegistry.add('forms.ArrayFieldStore', ArrayFieldStore);
FieldStoreRegistry.add('forms.PasswordFieldStore', PasswordFieldStore);
FieldStoreRegistry.add('forms.OptionFieldStore', OptionFieldStore);
FieldStoreRegistry.add('forms.EditorFieldStore', EditorFieldStore);
